import { z } from 'zod';

/**
 * Ingestion contract (CLAUDE.md §8.5, §9; plan/03-ingestion §5).
 *
 * Single source of truth shared by the cloud ingest route + queue consumer (apps/api)
 * AND the edge agent (mock now, real M10). The on-prem agent posts gzip NDJSON:
 *   envelope\n row\n row\n …
 * authenticated by Access service token + mTLS. `seq` is the idempotency key,
 * monotonic per `(site, source)`; dedupe drops anything `<=` the stored high-water mark.
 *
 * One-way boundary (§12.3): this is the agent's ONLY cloud target and it is
 * inbound-to-cloud only. The cloud never holds a path back to the agent.
 */

/** Sample/event quality flag, propagated end-to-end (§8.3). */
export const QualitySchema = z.enum(['good', 'uncertain', 'bad', 'substituted']);
export type Quality = z.infer<typeof QualitySchema>;

/** Aggregate resolution. `daily` carries the rollup fields in `agg`. */
export const ResolutionSchema = z.enum(['1m', '15m', 'daily']);
export type Resolution = z.infer<typeof ResolutionSchema>;

/** Event severity for forwarded alert/quality events. */
export const EventSeveritySchema = z.enum(['info', 'warn', 'critical']);
export type EventSeverity = z.infer<typeof EventSeveritySchema>;

/** Daily-only rollup fields (partial: the agent sends what it computed). */
export const IngestAggSchema = z
  .object({
    sum: z.number(),
    avg: z.number(),
    min: z.number(),
    max: z.number(),
    kwh: z.number(),
  })
  .partial();
export type IngestAgg = z.infer<typeof IngestAggSchema>;

/** Event-only payload (kind="event"). */
export const IngestEventSchema = z.object({
  severity: EventSeveritySchema,
  kind: z.string().min(1),
  message: z.string().min(1),
});
export type IngestEvent = z.infer<typeof IngestEventSchema>;

/**
 * One NDJSON row — an aggregated sample (`reading`) or an event (`event`).
 * `tag` is the tag *code*; the consumer resolves it to `tag_id`. `source_ts`
 * is the agent clock and WILL be clamped to the server window (§9); the row
 * retains both source_ts and the server's ingest_ts downstream.
 */
export const IngestRowSchema = z
  .object({
    kind: z.enum(['reading', 'event']),
    tag: z.string().min(1),
    res: ResolutionSchema, // ignored for kind=event
    source_ts: z.string().datetime(),
    value: z.number().nullable(),
    quality: QualitySchema,
    agg: IngestAggSchema.optional(), // daily-only
    event: IngestEventSchema.optional(), // event-only
  })
  .superRefine((row, ctx) => {
    if (row.kind === 'event' && !row.event) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['event'],
        message: 'event rows require an `event` payload',
      });
    }
  });
export type IngestRow = z.infer<typeof IngestRowSchema>;

/**
 * Batch envelope — the first NDJSON line. Carries dedupe identity
 * `(site, source, seq)`. `seq` is monotonic per `(site, source)` (§8.5).
 */
export const IngestBatchEnvelopeSchema = z.object({
  site: z.string().min(1), // site code
  source: z.string().min(1), // logical source within the site
  seq: z.number().int().nonnegative(), // idempotency key, monotonic per (site, source)
  count: z.number().int().positive(),
  sent_at: z.string().datetime(),
});
export type IngestBatchEnvelope = z.infer<typeof IngestBatchEnvelopeSchema>;

/** Decoded batch = envelope + its rows. */
export const IngestBatchSchema = z.object({
  envelope: IngestBatchEnvelopeSchema,
  rows: z.array(IngestRowSchema),
});
export type IngestBatch = z.infer<typeof IngestBatchSchema>;

/** Response to a successful POST /ingest (202). */
export const IngestAckSchema = z.object({
  accepted: z.number().int().nonnegative(),
  duplicates: z.number().int().nonnegative(),
  hwm: z.number().int().nonnegative(),
});
export type IngestAck = z.infer<typeof IngestAckSchema>;

/**
 * Queue message shape: a normalized row plus its dedupe identity and the server
 * ingest timestamp. The consumer upserts readings idempotently and forwards
 * live deltas + alerts to the SiteState DO (stub in M03, real in M04).
 */
export const IngestQueueMessageSchema = z.object({
  site: z.string().min(1),
  source: z.string().min(1),
  seq: z.number().int().nonnegative(),
  ingest_ts: z.string().datetime(), // server clock, set at enqueue
  row: IngestRowSchema,
});
export type IngestQueueMessage = z.infer<typeof IngestQueueMessageSchema>;

/** Live delta forwarded to the DO: latest value per tag (§7.3). */
export const SiteStateDeltaSchema = z.object({
  tag: z.string().min(1),
  ts: z.string().datetime(),
  value: z.number().nullable(),
  quality: QualitySchema,
});
export type SiteStateDelta = z.infer<typeof SiteStateDeltaSchema>;

/** Alert event forwarded to the DO (§7.3). */
export const SiteStateAlertSchema = z.object({
  tag: z.string().min(1),
  ts: z.string().datetime(),
  severity: EventSeveritySchema,
  kind: z.string().min(1),
  message: z.string().min(1),
});
export type SiteStateAlert = z.infer<typeof SiteStateAlertSchema>;

/** Limits (PERFORMANCE-BUDGETS §1). Enforced before parse work on the route. */
export const INGEST_MAX_BYTES = 5 * 1024 * 1024; // 5 MB gzipped-body cap
export const INGEST_MAX_ROWS = 10_000; // per-batch row cap
